// components/LanguageSelector.js
'use client'

import React, { useState, useRef, useEffect } from 'react'
import { useLanguage, languages } from '../contexts/LanguageContext'

const LanguageSelector = ({ className = '', variant = 'default' }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { currentLang, changeLanguage, isHydrated, isChanging } = useLanguage()
  const dropdownRef = useRef(null)

  // Dropdown tashqarisiga bosilganda yopish
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Language change events ni eshitish - yaxshilangan
  useEffect(() => {
    const handleLanguageChange = (event) => {
      // Force component re-render
      setIsOpen(false)
      // Force immediate state update
      setTimeout(() => {
        // Trigger additional re-render if needed
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('forceComponentUpdate'))
        }
      }, 50)
    }

    window.addEventListener('languageChanged', handleLanguageChange)
    return () => window.removeEventListener('languageChanged', handleLanguageChange)
  }, [])

  // Tilni o'zgartirish funktsiyasi - yaxshilangan
  const onChangeLanguage = (langCode) => {
    if (langCode !== currentLang) {
      changeLanguage(langCode)
      setIsOpen(false)
      
      // Immediate page refresh for complex components
      setTimeout(() => {
        // Force re-render of all components that might have cached data
        window.dispatchEvent(new CustomEvent('languageForceUpdate', {
          detail: { newLang: langCode, timestamp: Date.now() }
        }))
      }, 100)
    }
  }

  // Agar hali mount bo'lmagan bo'lsa, loading ko'rsatish
  if (!isHydrated) {
    return (
      <div className={`relative ${className}`}>
        <div className={getButtonStyle()}>
          <span className="w-5 h-5 bg-gray-200 rounded animate-pulse"></span>
          <span className="w-16 h-4 bg-gray-200 rounded animate-pulse"></span>
        </div>
      </div>
    )
  }

  // Hozirgi til ma'lumotlari (flag va nom) - force update bilan
  const currentLangInfo = languages[currentLang] || languages.uz

  // Tugma style variantlarga qarab
  function getButtonStyle() {
    const baseStyle = 'flex items-center gap-2 transition-all duration-200 border'
    
    switch (variant) {
      case 'navbar':
        return `${baseStyle} bg-white/10 backdrop-blur-md rounded-[12px] px-3 py-2 shadow-lg border-white/20 hover:bg-white/15`
      case 'mobile':
        return `${baseStyle} bg-white/15 backdrop-blur-md px-2 rounded-3xl shadow-lg border-white/25 hover:bg-white/20 w-[45px] h-[45px] text-white justify-center`
      case 'footer':
        return `${baseStyle} bg-gray-800 hover:bg-gray-700 rounded-lg px-3 py-2 border-gray-700`
      default:
        return `${baseStyle} bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-2 border-gray-200`
    }
  }

  // Dropdown style variantlarga qarab
  function getDropdownStyle() {
    switch (variant) {
      case 'navbar':
      case 'mobile':
        return 'absolute right-0 top-full mt-2 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-white/30 overflow-hidden min-w-[160px] z-[10001]'
      case 'footer':
        return 'absolute bottom-full left-0 mb-2 bg-gray-800 rounded-xl shadow-xl border border-gray-700 overflow-hidden min-w-[160px] z-[10001]'
      default:
        return 'absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden min-w-[160px] z-[10001]'
    }
  }

  // Matn rangi variantlarga qarab
  function getTextColor() {
    switch (variant) {
      case 'navbar':
      case 'mobile':
        return 'text-white/80'
      case 'footer':
        return 'text-gray-200'
      default:
        return 'text-gray-700'
    }
  }

  // Globe icon
  const GlobeIcon = ({ className }) => (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path d="M2 12h20" stroke="currentColor" strokeWidth="2" />
      <path
        d="M8 12c0-2.21.895-4.21 2.343-5.657M16 12c0 2.21-.895 4.21-2.343 5.657"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        className={`${getButtonStyle()} ${isChanging ? 'opacity-50' : 'opacity-100'}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Tilni tanlash"
        type="button"
        disabled={isChanging}
      >
        {variant === 'mobile' ? (
          <span className="text-lg">{currentLangInfo.flag}</span>
        ) : (
          <>
            <GlobeIcon
              className={`w-5 h-5 ${
                variant === 'navbar' ? 'text-white/70' : 'text-gray-500'
              }`}
            />
            <span className={`text-sm font-medium ${getTextColor()}`}>
              {currentLangInfo.flag} {variant !== 'mobile' && currentLangInfo.name}
            </span>
            <svg
              className={`w-4 h-4 ${
                variant === 'navbar'
                  ? 'text-white/60'
                  : variant === 'footer'
                  ? 'text-gray-400'
                  : 'text-gray-500'
              } transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </>
        )}
        
        {/* Language changing indicator */}
        {isChanging && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/20 rounded-lg">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
        )}
      </button>

      {isOpen && !isChanging && (
        <div className={getDropdownStyle()}>
          {Object.values(languages).map((lang) => (
            <button
              key={lang.code}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-200 ${
                currentLang === lang.code
                  ? variant === 'footer'
                    ? 'bg-gray-700 text-red-400 font-semibold'
                    : 'bg-red-50 text-red-600 font-semibold'
                  : variant === 'footer'
                  ? 'text-gray-200 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-50'
              } ${
                currentLang !== lang.code ? 'hover:scale-[1.02]' : ''
              }`}
              onClick={() => onChangeLanguage(lang.code)}
              type="button"
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="font-medium text-sm">{lang.name}</span>
              {currentLang === lang.code && (
                <svg
                  className={`w-4 h-4 ml-auto ${
                    variant === 'footer' ? 'text-red-400' : 'text-red-600'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LanguageSelector